import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, UploadCloud, Loader2, CheckCircle2, MapPin, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/apiClient";

export default function Apply() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ applicant_name: "", email: "", phone: "" });
  const [custom, setCustom] = useState({});
  const [file, setFile] = useState(null);

  useEffect(() => {
    api.get(`/public/job/${jobId}`).then(({ data }) => setJob(data)).catch(() => setError(true));
  }, [jobId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please attach your resume (PDF or DOCX)"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("applicant_name", form.applicant_name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("form_data", JSON.stringify(custom));
      fd.append("resume", file);
      await api.post(`/public/job/${jobId}/apply`, fd);
      setSubmitted(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-[#86868B]">This position is no longer available.</div>;
  if (!job) return <div className="min-h-screen flex items-center justify-center text-[#86868B]">Loading…</div>;

  const accent = job.accent_color || "#007AFF";

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7] px-5">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="apply-success"
        className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-black/5 p-10 text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 size={32} className="text-[#34C759]" strokeWidth={2} />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight">Application received!</h1>
        <p className="text-[#86868B] mt-2">Thanks for applying to <b>{job.title}</b> at {job.company}. Our team will review your profile shortly.</p>
        <button onClick={() => navigate(`/careers/${job.company_slug}`)} data-testid="back-to-careers"
          className="mt-6 text-white rounded-full px-6 py-3 font-semibold text-sm active:scale-95 transition-all hover:brightness-110"
          style={{ backgroundColor: accent }}>
          View more roles
        </button>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-10">
        <button onClick={() => navigate(`/careers/${job.company_slug}`)} data-testid="apply-back"
          className="flex items-center gap-1.5 text-sm font-semibold text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-6">
          <ArrowLeft size={16} /> Back to careers
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-sm font-semibold" style={{ color: accent }}>{job.company}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">{job.title}</h1>
          <div className="flex items-center gap-3 text-sm text-[#86868B] mt-2 flex-wrap">
            {job.location && <span className="flex items-center gap-1"><MapPin size={13} /> {job.location}</span>}
            {job.years_experience && <span>{job.years_experience}</span>}
            <span>{job.employment_type}</span>
          </div>

          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-6 mt-6">
            <h2 className="font-bold tracking-tight mb-2">About the role</h2>
            <p className="text-sm text-[#1D1D1F] leading-relaxed whitespace-pre-wrap">{job.description}</p>
            {job.min_qualification && <p className="text-sm text-[#86868B] mt-3"><b className="text-[#1D1D1F]">Minimum qualification:</b> {job.min_qualification}</p>}
          </div>
        </motion.div>

        <form onSubmit={submit} className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-6 mt-5 space-y-4">
          <h2 className="text-lg font-extrabold tracking-tight">Your application</h2>
          <Fld label="Full name" value={form.applicant_name} onChange={(v) => setForm({ ...form, applicant_name: v })} testId="apply-name" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Fld label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="apply-email" />
            <Fld label="Phone (with country code)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="apply-phone" required={false} placeholder="+1 555 000 0000" />
          </div>

          {(job.custom_fields || []).map((cf, i) => (
            <div key={i}>
              <label className="text-sm font-semibold">{cf.label}{cf.required && <span className="text-[#FF3B30]"> *</span>}</label>
              {cf.type === "textarea" ? (
                <textarea rows={3} required={cf.required} value={custom[cf.label] || ""} onChange={(e) => setCustom({ ...custom, [cf.label]: e.target.value })} data-testid={`apply-custom-${i}`}
                  className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors resize-none" />
              ) : (
                <input type={cf.type === "number" ? "number" : "text"} required={cf.required} value={custom[cf.label] || ""} onChange={(e) => setCustom({ ...custom, [cf.label]: e.target.value })} data-testid={`apply-custom-${i}`}
                  className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors" />
              )}
            </div>
          ))}

          <div>
            <label className="text-sm font-semibold">Resume (PDF or DOCX)</label>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc" className="hidden" data-testid="apply-resume"
              onChange={(e) => setFile(e.target.files[0])} />
            <button type="button" onClick={() => fileRef.current.click()} data-testid="apply-resume-btn"
              className={`mt-1 w-full border-2 border-dashed rounded-2xl px-4 py-6 flex flex-col items-center gap-2 transition-colors ${file ? "border-[#34C759]/40 bg-[#34C759]/5" : "border-black/10 bg-gray-50/50 hover:border-[#007AFF]/40 hover:bg-[#007AFF]/[0.03]"}`}>
              {file ? <FileText size={24} className="text-[#34C759]" /> : <UploadCloud size={24} className="text-[#86868B]" />}
              <span className="text-sm font-semibold">{file ? file.name : "Click to upload your resume"}</span>
              <span className="text-xs text-[#86868B]">Max 10MB</span>
            </button>
          </div>

          <button type="submit" disabled={submitting} data-testid="apply-submit"
            className="w-full text-white rounded-full px-6 py-3.5 font-semibold active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60 hover:brightness-110"
            style={{ backgroundColor: accent }}>
            {submitting && <Loader2 size={17} className="animate-spin" />} Submit application
          </button>
        </form>
        <p className="text-center text-xs text-[#A1A1A6] mt-6">Powered by HireScope</p>
      </div>
    </div>
  );
}

function Fld({ label, value, onChange, type = "text", testId, required = true, placeholder }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}{required && <span className="text-[#FF3B30]"> *</span>}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
        className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors" />
    </div>
  );
}
